import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, ButtonBase, Chip, Paper, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import styles from '../integration-wizard/steps/step-2-experience-selection/ExperienceSelectionStep.module.scss';

const useStyles = makeStyles((theme) => ({
  card: { cursor: 'default' },
  selection: {
    '&&': {
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      width: '100%',
      padding: theme.spacing(3),
      textAlign: 'left',
    },
    '&:disabled': { opacity: 0.6 },
  },
  footer: {
    padding: theme.spacing(0, 3, 3),
  },
}));

export default function PresetCard({ option, selected, disabled, previewDisabled, onSelect, onPreview, t }) {
  const classes = useStyles();
  return (
    <Paper component="article" elevation={0} className={`${styles.experienceCard} ${classes.card} ${selected ? styles.selected : ''}`}>
      <Box className={styles.imageContainer}>
        <img src={option.image} alt="" width="680" height="380" />
      </Box>
      <ButtonBase
        className={`${styles.cardContent} ${classes.selection}`}
        onClick={onSelect}
        disabled={disabled}
        aria-pressed={selected}
        aria-labelledby={`preset-${option.type}-title`}
        aria-describedby={`preset-${option.type}-description`}
        data-testid={`search-mode-${option.type}`}
      >
        <Box component="span" className={styles.cardEyebrow}>
          {selected
            ? <RadioButtonCheckedIcon color="primary" aria-hidden="true" />
            : <RadioButtonUncheckedIcon color="disabled" aria-hidden="true" />}
          {option.recommended && (
            <Chip
              component="span"
              label={t('wizard.searchSetup.experience.recommended')}
              size="small"
              className={styles.recommendedChip}
            />
          )}
        </Box>
        <Typography
          component="span"
          display="block"
          className={styles.cardTitle}
          id={`preset-${option.type}-title`}
        >
          {t(option.nameKey)}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          className={styles.cardDescription}
          id={`preset-${option.type}-description`}
        >
          {t(option.descriptionKey)}
        </Typography>
        <Box component="span" role="list" className={styles.featuresList}>
          {option.features.map((featureKey) => (
            <Typography component="span" role="listitem" variant="body2" className={styles.feature} key={featureKey}>
              {t(featureKey)}
            </Typography>
          ))}
        </Box>
      </ButtonBase>
      <Box className={classes.footer}>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<OpenInNewIcon />}
          onClick={onPreview}
          disabled={disabled || previewDisabled}
          data-testid={`preset-preview-${option.type}`}
          aria-label={`${t('search.presets.livePreview')} — ${t(option.nameKey)}`}
        >
          {t('search.presets.livePreview')}
        </Button>
      </Box>
    </Paper>
  );
}

PresetCard.propTypes = {
  option: PropTypes.shape({
    type: PropTypes.string.isRequired,
    nameKey: PropTypes.string.isRequired,
    descriptionKey: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    image: PropTypes.string.isRequired,
    recommended: PropTypes.bool.isRequired,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  previewDisabled: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
